import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCellValue } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Users,
  Gift,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Pencil,
  Loader2,
  Save,
  Eye,
  UserPlus,
  Percent,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';

export default function ReferralManagementPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('programs');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data
  const [programs, setPrograms] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  
  // Dialog state
  const [programDialog, setProgramDialog] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    program_type: 'customer',
    description: '',
    referrer_reward_on_signup: 5,
    referrer_reward_on_conversion: 50,
    referee_trial_extension_days: 7,
    referee_discount_percent: 20,
    referee_credit_amount: 0,
    max_referrals_per_month: 10,
    requires_approval: false,
    min_reward_for_approval: 50,
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [programsRes, pendingRes, analyticsRes] = await Promise.all([
        api.get('/referrals/programs?include_inactive=true'),
        api.get('/referrals/pending-approvals'),
        api.get('/referrals/analytics?days=30'),
      ]);
      
      setPrograms(programsRes.data.programs || []);
      setPendingApprovals(pendingRes.data.pending || []);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Failed to load referral data:', err);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProgram = (program) => {
    setSelectedProgram(program);
    setFormData({
      name: program.name,
      program_type: program.program_type,
      description: program.description || '',
      referrer_reward_on_signup: program.referrer_reward_on_signup,
      referrer_reward_on_conversion: program.referrer_reward_on_conversion,
      referee_trial_extension_days: program.referee_trial_extension_days,
      referee_discount_percent: program.referee_discount_percent,
      referee_credit_amount: program.referee_credit_amount,
      max_referrals_per_month: program.max_referrals_per_month,
      requires_approval: program.requires_approval === 1,
      min_reward_for_approval: program.min_reward_for_approval,
      is_active: program.is_active === 1,
    });
    setProgramDialog(true);
  };

  const handleAddProgram = () => {
    setSelectedProgram(null);
    setFormData({
      name: '',
      program_type: 'customer',
      description: '',
      referrer_reward_on_signup: 5,
      referrer_reward_on_conversion: 50,
      referee_trial_extension_days: 7,
      referee_discount_percent: 20,
      referee_credit_amount: 0,
      max_referrals_per_month: 10,
      requires_approval: false,
      min_reward_for_approval: 50,
      is_active: true,
    });
    setProgramDialog(true);
  };

  const handleSaveProgram = async () => {
    if (!formData.name.trim()) {
      toast.error('Program name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (selectedProgram) {
        await api.put(`/referrals/programs/${selectedProgram.id}`, formData);
        toast.success('Program updated');
      } else {
        await api.post('/referrals/programs', formData);
        toast.success('Program created');
      }
      setProgramDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save program');
    } finally {
      setSaving(false);
    }
  };

  const handleApproval = async (approved) => {
    if (!selectedReferral) return;
    
    setSaving(true);
    try {
      await api.post(`/referrals/approve/${selectedReferral.id}`, {
        approved,
        notes: '',
      });
      toast.success(approved ? 'Referral approved' : 'Referral rejected');
      setApprovalDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageSkeleton maxWidth="7xl" kpis={4} tabs sections={2} data-testid="referral-management-loading" />
    );
  }

  if (!user?.is_superadmin) {
    return (
      <PageShell maxWidth="7xl" contentClassName="flex min-h-[400px] flex-col items-center justify-center text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-muted-foreground">Only superadmins can access this page.</p>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="7xl" data-testid="referral-management-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Referral Programs</h1>
        <p className="text-muted-foreground">
          Manage referral programs and approve rewards
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Total Referrals</span>
            </div>
            <p className="text-2xl font-bold mt-1">
              {analytics?.stats?.total_referrals || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-muted-foreground">Conversions</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-emerald-600">
              {analytics?.stats?.conversions || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-muted-foreground">Rewards Paid</span>
            </div>
            <p className="text-2xl font-bold mt-1">
              ${analytics?.stats?.total_rewards_paid?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-muted-foreground">Pending Approvals</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-amber-600">
              {pendingApprovals.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="programs" data-testid="tab-programs">
            <Gift className="h-4 w-4 mr-2" />
            Programs
          </TabsTrigger>
          <TabsTrigger value="approvals" data-testid="tab-approvals">
            <CheckCircle className="h-4 w-4 mr-2" />
            Pending Approvals
            {pendingApprovals.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {pendingApprovals.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <TrendingUp className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Programs Tab */}
        <TabsContent value="programs" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Referral Programs</CardTitle>
                <CardDescription>
                  Configure reward structures for different referrer types
                </CardDescription>
              </div>
              <Button onClick={handleAddProgram} data-testid="add-program-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Program
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Program</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Referrer Rewards</TableHead>
                    <TableHead className="text-center">Referee Benefits</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {programs.map((program) => (
                    <TableRow key={program.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{program.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {program.description || 'No description'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={program.program_type === 'trial' ? 'secondary' : 'default'}>
                          {program.program_type === 'trial' ? 'Trial Advocate' : 'Customer'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-sm">
                          {program.referrer_reward_on_signup > 0 && (
                            <p className="text-emerald-600">
                              ${program.referrer_reward_on_signup} on signup
                            </p>
                          )}
                          {program.referrer_reward_on_conversion > 0 && (
                            <p className="text-emerald-600 font-medium">
                              ${program.referrer_reward_on_conversion} on conversion
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-sm">
                          {program.referee_trial_extension_days > 0 && (
                            <p>+{program.referee_trial_extension_days} trial days</p>
                          )}
                          {program.referee_discount_percent > 0 && (
                            <p>{program.referee_discount_percent}% off first month</p>
                          )}
                          {program.referee_credit_amount > 0 && (
                            <p>${program.referee_credit_amount} credit</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={program.is_active ? 'default' : 'secondary'} className={program.is_active ? 'bg-emerald-500' : ''}>
                          {program.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEditProgram(program)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {programs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No referral programs configured. Create one to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approvals Tab */}
        <TabsContent value="approvals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Reward Approvals</CardTitle>
              <CardDescription>
                Review and approve high-value referral rewards
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Referrer</TableHead>
                    <TableHead>Referee</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Reward Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingApprovals.map((referral) => (
                    <TableRow key={referral.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{formatCellValue(referral.referrer_name)}</p>
                          <p className="text-xs text-muted-foreground">{formatCellValue(referral.referrer_email)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{formatCellValue(referral.referee_email)}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{formatCellValue(referral.program_name)}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={referral.status === 'converted' ? 'default' : 'secondary'}>
                          {referral.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        ${referral.referrer_reward_amount?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedReferral(referral);
                              setApprovalDialog(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {pendingApprovals.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No pending approvals
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Top Referrers (30 days)</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="text-center">Referrals</TableHead>
                      <TableHead className="text-right">Earned</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(analytics?.top_referrers || []).map((referrer, idx) => (
                      <TableRow key={referrer.referrer_id || idx}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{formatCellValue(referrer.name)}</p>
                            <p className="text-xs text-muted-foreground">{formatCellValue(referrer.email)}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{referrer.referral_count}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          ${referrer.total_earned?.toFixed(2) || '0.00'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!analytics?.top_referrers || analytics.top_referrers.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-4 text-muted-foreground">
                          No referral data yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Performance by Program</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Program</TableHead>
                      <TableHead className="text-center">Referrals</TableHead>
                      <TableHead className="text-center">Conversions</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(analytics?.by_program || []).map((prog, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <p className="font-medium">{formatCellValue(prog.name)}</p>
                          <Badge variant="outline" className="text-xs">
                            {prog.program_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">{formatCellValue(prog.referral_count)}</TableCell>
                        <TableCell className="text-center text-emerald-600">{formatCellValue(prog.conversions)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {prog.referral_count > 0 
                            ? ((prog.conversions / prog.referral_count) * 100).toFixed(1) + '%'
                            : '0%'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!analytics?.by_program || analytics.by_program.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                          No program data yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Program Edit/Create Dialog */}
      <Dialog open={programDialog} onOpenChange={setProgramDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedProgram ? 'Edit Referral Program' : 'Create Referral Program'}
            </DialogTitle>
            <DialogDescription>
              Configure the reward structure for this referral program
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Program Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Customer Referral Bonus"
                />
              </div>
              <div className="space-y-2">
                <Label>Program Type</Label>
                <Select
                  value={formData.program_type}
                  onValueChange={(v) => setFormData({ ...formData, program_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer Referral</SelectItem>
                    <SelectItem value="trial">Trial Advocate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the program"
              />
            </div>

            {/* Referrer Rewards */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Gift className="h-4 w-4 text-emerald-500" />
                Referrer Rewards (Cash Credits)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Reward on Signup ($)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      className="pl-9"
                      value={formData.referrer_reward_on_signup}
                      onChange={(e) => setFormData({ ...formData, referrer_reward_on_signup: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">When referee signs up for trial</p>
                </div>
                <div className="space-y-2">
                  <Label>Reward on Conversion ($)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      className="pl-9"
                      value={formData.referrer_reward_on_conversion}
                      onChange={(e) => setFormData({ ...formData, referrer_reward_on_conversion: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">When referee converts to paid</p>
                </div>
              </div>
            </div>

            {/* Referee Benefits */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-blue-500" />
                Referee Benefits (What they get)
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Extra Trial Days</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.referee_trial_extension_days}
                    onChange={(e) => setFormData({ ...formData, referee_trial_extension_days: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>First Month Discount (%)</Label>
                  <div className="relative">
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.referee_discount_percent}
                      onChange={(e) => setFormData({ ...formData, referee_discount_percent: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Account Credit ($)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min="0"
                      className="pl-9"
                      value={formData.referee_credit_amount}
                      onChange={(e) => setFormData({ ...formData, referee_credit_amount: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3">Program Controls</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Referrals per Month</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_referrals_per_month}
                    onChange={(e) => setFormData({ ...formData, max_referrals_per_month: parseInt(e.target.value) || 10 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Auto-approve Below ($)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min="0"
                      className="pl-9"
                      value={formData.min_reward_for_approval}
                      onChange={(e) => setFormData({ ...formData, min_reward_for_approval: parseFloat(e.target.value) || 50 })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <div className="space-y-0.5">
                  <Label>Require Manual Approval</Label>
                  <p className="text-xs text-muted-foreground">For rewards above threshold</p>
                </div>
                <Switch
                  checked={formData.requires_approval}
                  onCheckedChange={(v) => setFormData({ ...formData, requires_approval: v })}
                />
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <div className="space-y-0.5">
                  <Label>Program Active</Label>
                  <p className="text-xs text-muted-foreground">Users can generate referral codes</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProgramDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveProgram} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {selectedProgram ? 'Save Changes' : 'Create Program'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Review Dialog */}
      <Dialog open={approvalDialog} onOpenChange={setApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Referral Reward</DialogTitle>
            <DialogDescription>
              Approve or reject this referral reward
            </DialogDescription>
          </DialogHeader>

          {selectedReferral && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Referrer</Label>
                  <p className="font-medium">{formatCellValue(selectedReferral.referrer_name)}</p>
                  <p className="text-sm text-muted-foreground">{selectedReferral.referrer_email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Referee</Label>
                  <p className="font-medium">{selectedReferral.referee_email}</p>
                  <Badge className="mt-1">{selectedReferral.status}</Badge>
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20">
                <Label className="text-muted-foreground">Reward Amount</Label>
                <p className="text-2xl font-bold text-emerald-600">
                  ${selectedReferral.referrer_reward_amount?.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Will be credited to referrer&apos;s account
                </p>
              </div>
              
              {selectedReferral.fraud_flags && JSON.parse(selectedReferral.fraud_flags).length > 0 && (
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="h-4 w-4" />
                    <Label>Fraud Flags</Label>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {JSON.parse(selectedReferral.fraud_flags).map((flag, idx) => (
                      <Badge key={idx} variant="outline" className="text-amber-600 border-amber-300">
                        {flag.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleApproval(false)}
              disabled={saving}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              onClick={() => handleApproval(true)}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
